"use client";

import { IconCaretDownFill } from "@decibeltrade/react-icons";
import { useState } from "react";
import { css } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { RPC_URLS } from "@/constants/chain-rpc";
import { type EvmChainName } from "@/constants/chains";

import { Menu, MenuItem } from "./menu";
import { ArbLogo } from "./vectors/chains/arb";
import { BaseLogo } from "./vectors/chains/base";
import { EthLogo } from "./vectors/chains/eth";

// Full EVM chain configuration (includes UI fields like Icons)
export const EVM_CHAINS = [
  {
    id: "ethereum",
    name: "Ethereum" satisfies EvmChainName,
    chainId: 1,
    testnetChainId: 11155111, // Sepolia
    Icon: EthLogo,
    rpcUrl: RPC_URLS.Ethereum,
    blockExplorer: "https://etherscan.io",
    currencySymbol: "ETH",
  },
  {
    id: "arbitrum",
    name: "Arbitrum" satisfies EvmChainName,
    chainId: 42161,
    testnetChainId: 421614, // Arbitrum Sepolia
    Icon: ArbLogo,
    rpcUrl: RPC_URLS.Arbitrum,
    blockExplorer: "https://arbiscan.io",
    currencySymbol: "ETH",
  },
  {
    id: "base",
    name: "Base" satisfies EvmChainName,
    chainId: 8453,
    testnetChainId: 84532, // Base Sepolia
    Icon: BaseLogo,
    rpcUrl: RPC_URLS.Base,
    blockExplorer: "https://basescan.org",
    currencySymbol: "ETH",
  },
] as const;

export type EvmChainConfig = (typeof EVM_CHAINS)[number];
export type EvmChainId = (typeof EVM_CHAINS)[number]["id"];

export function getEvmChainById(chainId: EvmChainId): EvmChainConfig | undefined {
  return EVM_CHAINS.find((chain) => chain.id === chainId);
}

interface ChainSelectorMenuProps {
  selectedChain?: EvmChainId;
  onChainSelect?: (chainId: EvmChainId) => void;
}

export function EVMChainSelectorMenu({ selectedChain, onChainSelect }: ChainSelectorMenuProps) {
  const [currentChain, setCurrentChain] = useState<EvmChainId>(selectedChain || "arbitrum");

  const handleChainSelect = (chainId: EvmChainId) => {
    setCurrentChain(chainId);
    onChainSelect?.(chainId);
  };

  const selectedChainData = EVM_CHAINS.find((chain) => chain.id === currentChain);
  const SelectedIcon = selectedChainData?.Icon;

  const menuItems: MenuItem[] = EVM_CHAINS.map((chain) => {
    const ChainIcon = chain.Icon;
    return {
      id: chain.id,
      label: (
        <div className={flex({ gap: "8", align: "center" })}>
          <ChainIcon className={css({ w: "16", h: "16" })} />
          <span>{chain.name}</span>
        </div>
      ),
      onSelect: () => handleChainSelect(chain.id),
    };
  });

  return (
    <div className={css({ w: "full" })}>
      <Menu
        trigger={
          <button
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "8",
              px: "12",
              py: "8",
              borderRadius: "200",
              bg: "background.secondary",
              border: "1px solid",
              borderColor: "border.primary",
              cursor: "pointer",
              w: "full",
              justifyContent: "space-between",
              _hover: {
                bg: "background.tertiary",
              },
              transitionPreset: "colors",
            })}
          >
            <div className={flex({ gap: "8", align: "center" })}>
              {SelectedIcon && <SelectedIcon className={css({ w: "16", h: "16" })} />}
              <span className={css({ textStyle: "body.md", color: "text.primary" })}>
                {selectedChainData?.name}
              </span>
            </div>
            <IconCaretDownFill className={css({ w: "16", h: "16", color: "text.tertiary" })} />
          </button>
        }
        menuItems={menuItems}
        size="md"
        placement="bottom-start"
        portalled={false}
        className={css({ w: "full" })}
      />
    </div>
  );
}
